import { http } from './http';
import { ApiError } from './errors';
import { arr, normCertStatus, normEmisor, normEstablecimiento, normPunto, pick, str, type CertValidationStatus, type Emisor, type Establecimiento, type PuntoEmision } from './types';

type R = Record<string, unknown>;

/**
 * A 404 on a fallback list endpoint means "this backend version does not expose it", which is the
 * only failure we may treat as "no rows". Anything else — an abort, a 401, a 5xx, a dead network —
 * must propagate: swallowing it returns an emisor with no establecimientos, which is
 * indistinguishable from a brand-new account and bounces the user into onboarding.
 */
function isEndpointAbsent(e: unknown): boolean {
  return e instanceof ApiError && e.isNotFound;
}

export interface EmisorBody {
  ruc: string;
  razonSocial: string;
  nombreComercial?: string | null;
  direccionMatriz?: string | null;
  obligadoContabilidad: 'SI' | 'NO';
}

export interface CertificateStatus {
  isSignatureConfigured: boolean;
  certificateUploadedAt: string | null;
  validationStatus: CertValidationStatus;
  validatedAt: string | null;
  certificateExpiresAt: string | null;
}

function parseCertStatus(j: R): CertificateStatus {
  return {
    isSignatureConfigured: pick(j, 'isSignatureConfigured', 'is_signature_configured') === true,
    certificateUploadedAt: str(pick(j, 'certificateUploadedAt', 'certificate_uploaded_at')),
    validationStatus: normCertStatus(pick(j, 'validationStatus', 'validation_status')),
    validatedAt: str(pick(j, 'validatedAt', 'validated_at')),
    certificateExpiresAt: str(pick(j, 'certificateExpiresAt', 'certificate_expires_at')),
  };
}

export const emisorApi = {
  /**
   * GET /emisor — source of truth incl. establecimientos + puntos. null when not configured (404 / no ruc).
   * Some backend versions return establecimientos without their nested puntos; in that case each
   * establecimiento's points are fetched from GET /establecimientos/:id/puntos and merged in.
   */
  async get(signal?: AbortSignal): Promise<Emisor | null> {
    let emisor: Emisor | null;
    try {
      const j = await http.get<R | null>('/emisor', { signal });
      if (!j || !str(pick(j, 'ruc'))) return null;
      emisor = normEmisor(j);
    } catch (e) {
      if (e instanceof ApiError && e.isNotFound) return null;
      throw e;
    }
    if (emisor.establecimientos.length === 0) {
      // Older/alternate shape: establecimientos only via the list endpoint.
      try {
        emisor = { ...emisor, establecimientos: await emisorApi.listEstablecimientos(signal) };
      } catch (e) {
        if (!isEndpointAbsent(e)) throw e;
        /* endpoint absent on this backend: keep what /emisor gave us */
      }
    }
    const hydrated = await Promise.all(
      emisor.establecimientos.map(async (e) => {
        if (e.puntos.length > 0 || !e.id) return e;
        try {
          const puntos = await emisorApi.listPuntos(e.id, signal);
          return { ...e, puntos: puntos.map((p) => ({ ...p, establecimientoId: p.establecimientoId ?? e.id, establecimientoCodigo: p.establecimientoCodigo ?? e.codigo })) };
        } catch (err) {
          if (!isEndpointAbsent(err)) throw err;
          return e;
        }
      }),
    );
    return { ...emisor, establecimientos: hydrated };
  },

  /** GET /emisor/metadata — lightweight scalars + presigned logo URL. */
  async metadata(signal?: AbortSignal): Promise<Emisor | null> {
    try {
      const j = await http.get<R | null>('/emisor/metadata', { signal });
      if (!j || !str(pick(j, 'ruc'))) return null;
      return normEmisor(j);
    } catch (e) {
      if (e instanceof ApiError && e.isNotFound) return null;
      throw e;
    }
  },

  put: (body: EmisorBody) => http.put<R>('/emisor', body).then((j) => (j ? normEmisor(j) : null)),
  patch: (body: Partial<EmisorBody> & { ambiente?: '1' | '2' }) => http.patch<R>('/emisor', body).then((j) => (j ? normEmisor(j) : null)),

  /* --- certificate (.p12): 3 steps --- */
  certificateUploadUrl: (ruc: string) =>
    http.post<R>('/emisor/certificate/upload-url', { ruc }).then((j) => ({ uploadUrl: String(j.uploadUrl ?? ''), key: str(j.key), expiresIn: Number(j.expiresIn ?? 0) })),

  /** Raw PUT to S3: bytes only, NO Content-Type or Authorization (signed-header mismatch → 403). */
  async uploadToS3(uploadUrl: string, bytes: ArrayBuffer, contentType?: string): Promise<void> {
    const res = await fetch(uploadUrl, { method: 'PUT', body: bytes, headers: contentType ? { 'Content-Type': contentType } : undefined });
    if (res.status !== 200 && res.status !== 204) throw new ApiError(res.status, `Carga a S3 rechazada (${res.status})`, 'S3_UPLOAD_FAILED');
  },

  certificateConfirm: (ruc: string, password: string) => http.post<R>('/emisor/certificate/confirm', { ruc, password }).then((j) => parseCertStatus(j ?? {})),

  /* --- logo --- */
  logoUploadUrl: (ruc: string) =>
    http.post<R>('/emisor/logo/upload-url', { ruc }).then((j) => ({
      uploadUrl: String(j.uploadUrl ?? ''),
      key: str(j.key),
      contentType: str(j.contentType ?? j.content_type),
      maxBytes: Number(j.maxBytes ?? 2 * 1024 * 1024),
    })),
  logoConfirm: (ruc: string) => http.post<R>('/emisor/logo/confirm', { ruc }),
  logoUrl: (ruc: string) => http.get<R>('/emisor/logo', { query: { ruc } }).then((j) => str(j?.url)),
  logoDelete: (ruc: string) => http.delete<R>('/emisor/logo', { query: { ruc } }),

  /* --- establecimientos & puntos --- */
  async listEstablecimientos(signal?: AbortSignal): Promise<Establecimiento[]> {
    const j = await http.get<unknown>('/establecimientos', { signal });
    return arr(Array.isArray(j) ? j : (j as R)?.data).map(normEstablecimiento);
  },
  createEstablecimiento: (body: { codigo: string; nombreComercial: string; direccion: string; isDefault: boolean }) =>
    http.post<R>('/establecimientos', body).then((j) => (j ? normEstablecimiento(j) : null)),
  updateEstablecimiento: (id: string, body: { codigo: string; nombreComercial: string; direccion: string; isDefault: boolean }) =>
    http.put<R>(`/establecimientos/${encodeURIComponent(id)}`, body).then((j) => (j ? normEstablecimiento(j) : null)),
  deleteEstablecimiento: (id: string) => http.delete<unknown>(`/establecimientos/${encodeURIComponent(id)}`),

  async listPuntos(establecimientoId: string, signal?: AbortSignal): Promise<PuntoEmision[]> {
    const j = await http.get<unknown>(`/establecimientos/${encodeURIComponent(establecimientoId)}/puntos`, { signal });
    return arr(Array.isArray(j) ? j : (j as R)?.data).map((p) => normPunto(p));
  },
  createPunto: (establecimientoId: string, body: { codigo: string; descripcion: string; isDefault: boolean }) =>
    http.post<R>(`/establecimientos/${encodeURIComponent(establecimientoId)}/puntos`, body).then((j) => (j ? normPunto(j) : null)),
  updatePunto: (id: string, body: { codigo: string; descripcion: string; isDefault: boolean }) =>
    http.put<R>(`/puntos/${encodeURIComponent(id)}`, body).then((j) => (j ? normPunto(j) : null)),
  deletePunto: (id: string) => http.delete<unknown>(`/puntos/${encodeURIComponent(id)}`),
};
